/**
 * @file ProfileSwitcher.tsx
 * @description Dropdown component for switching between athlete profiles
 * Displays in the sidebar and allows coaches to manage multiple athletes
 */

import { useState, useRef, useEffect } from 'react';
import { useStore, selectActiveProfile, selectIsCoach } from '@/lib/store';
import { Badge } from '@/components/ui/badge';
import {
    Check,
    ChevronDown,
    Crown,
    ExternalLink,
    Settings,
    User,
    UserPlus,
    Users
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import type { ProfileSummary } from '@/types';

interface ProfileSwitcherProps {
    onAddAthlete?: () => void;
    onManageProfiles?: () => void;
}

export function ProfileSwitcher({ onAddAthlete, onManageProfiles }: ProfileSwitcherProps) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const {
        athlete,
        activeProfileId,
        getProfileSummaries,
        switchProfile,
    } = useStore();
    const navigate = useNavigate();

    const activeProfile = useStore(selectActiveProfile);
    const isCoach = useStore(selectIsCoach);
    const userRole = useStore(state => state.userRole);
    const profiles = getProfileSummaries();

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Close on escape key
    useEffect(() => {
        function handleEscape(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    const handleProfileSelect = (profileId: string) => {
        switchProfile(profileId);
        setIsOpen(false);
        navigate('/settings');
    };

    const handleAddAthlete = () => {
        setIsOpen(false);
        onAddAthlete?.();
    };

    const handleManageProfiles = () => {
        setIsOpen(false);
        onManageProfiles?.();
    };

    // Get display info
    const displayName = activeProfile?.displayName || athlete.name || 'No Profile';
    const sport = activeProfile?.athlete.sport || athlete.sport;
    const relation = activeProfile?.relation || 'self';
    const isLocal = activeProfile?.intervalsAthleteId?.startsWith('local-');

    // If no profiles exist yet, show a simpler view
    if (profiles.length === 0 && !athlete.name) {
        return (
            <div className="flex items-center gap-3 px-2 mb-6">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-400">
                    <User className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm text-muted-foreground">No profile set</p>
                    <p className="text-xs text-muted-foreground">Complete onboarding</p>
                </div>
            </div>
        );
    }

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Trigger Button */}
            <button
                onClick={() => {
                    if (isCoach || profiles.length > 1 || userRole === 'admin' || userRole === 'superadmin') {
                        setIsOpen(!isOpen);
                    } else {
                        navigate('/settings');
                    }
                }}
                className={cn(
                    'w-full flex items-center gap-3 px-2 py-2 rounded-lg transition-colors group',
                    'hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-primary/20',
                    isOpen && 'bg-black/5'
                )}
            >
                {/* Avatar */}
                <div className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm overflow-hidden',
                    relation === 'self'
                        ? 'bg-primary/10 text-primary'
                        : 'bg-blue-100 text-blue-600'
                )}>
                    {activeProfile?.athlete.profilePicture || athlete.profilePicture ? (
                        <img
                            src={activeProfile?.athlete.profilePicture || athlete.profilePicture}
                            alt={displayName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                // Fallback to initial on error
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.parentElement!.textContent = displayName.charAt(0).toUpperCase();
                            }}
                        />
                    ) : (
                        displayName.charAt(0).toUpperCase()
                    )}
                </div>

                {/* Name & Sport */}
                <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{displayName}</p>
                        {activeProfile?.athlete.isSupporter && (
                            <Crown className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                        )}
                        {isLocal && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-gray-100 text-gray-600 border-gray-200">
                                Local
                            </Badge>
                        )}
                        {!isLocal && relation === 'coached' && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                Coached
                            </Badge>
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{sport}</p>
                </div>

                {/* Navigation / Chevron Icon */}
                {isCoach || profiles.length > 1 || userRole === 'admin' || userRole === 'superadmin' ? (
                    <ChevronDown className={cn(
                        'w-4 h-4 text-muted-foreground transition-transform',
                        isOpen && 'rotate-180'
                    )} />
                ) : (
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                )}
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border border-border overflow-hidden z-50 animate-in fade-in slide-in-from-bottom-2 duration-200 w-[260px] md:w-full">
                    {/* Profile List */}
                    <div className="max-h-64 overflow-y-auto">
                        {profiles.map((profile) => (
                            <ProfileItem
                                key={profile.id}
                                profile={profile}
                                isActive={profile.id === activeProfileId}
                                isPending={!!profile.inviteToken}
                                onSelect={() => {
                                    if (profile.inviteToken) {
                                        // Show a toast or just do nothing for pending
                                        return;
                                    }
                                    handleProfileSelect(profile.id);
                                }}
                            />
                        ))}
                    </div>

                    {/* Divider */}
                    <div className="border-t border-border" />

                    {/* Actions */}
                    <div className="p-1">
                        {/* Coach/Admin Actions */}
                        {(isCoach || activeProfile?.relation === 'coached' || userRole === 'admin' || userRole === 'superadmin') && (
                            <>
                                <button
                                    onClick={handleAddAthlete}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded hover:bg-muted transition-colors"
                                >
                                    <UserPlus className="w-4 h-4 text-muted-foreground" />
                                    <span>Add Athlete</span>
                                </button>

                                {profiles.length > 0 && (
                                    <button
                                        onClick={handleManageProfiles}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded hover:bg-muted transition-colors"
                                    >
                                        <Settings className="w-4 h-4 text-muted-foreground" />
                                        <span>Manage Profiles</span>
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// Individual profile item in the dropdown
function ProfileItem({
    profile,
    isActive,
    onSelect,
    isPending: _isPending,
}: {
    profile: ProfileSummary;
    isActive: boolean;
    onSelect: () => void;
    isPending?: boolean; // Keep type as is, just rename usage
}) {
    const isLocal = profile.intervalsAthleteId?.startsWith('local-');

    return (
        <button
            onClick={onSelect}
            className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors',
                isActive ? 'bg-primary/5' : 'hover:bg-muted'
            )}
        >
            {/* Avatar */}
            <div className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center font-medium text-xs overflow-hidden',
                profile.relation === 'self'
                    ? 'bg-primary/10 text-primary'
                    : 'bg-blue-100 text-blue-600'
            )}>
                {profile.profilePicture ? (
                    <img
                        src={profile.profilePicture}
                        alt={profile.displayName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            // Fallback to initial on error
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.parentElement!.textContent = profile.displayName.charAt(0).toUpperCase();
                        }}
                    />
                ) : (
                    profile.displayName.charAt(0).toUpperCase()
                )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{profile.displayName}</span>
                    {profile.isSupporter && (
                        <Crown className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                    )}
                    {isLocal && (
                        <Badge variant="outline" className="text-[8px] px-1 py-0 bg-gray-50 text-gray-500 border-gray-200">
                            LOCAL
                        </Badge>
                    )}
                    {!isLocal && profile.relation === 'coached' && !profile.inviteToken && (
                        <Users className="w-3 h-3 text-blue-500" />
                    )}
                    {profile.inviteToken && (
                        <Badge variant="outline" className="text-[8px] px-1 py-0 bg-amber-50 text-amber-600 border-amber-200 uppercase tracking-wider">
                            Pending
                        </Badge>
                    )}
                </div>
                <span className="text-xs text-muted-foreground">{profile.sport}</span>
            </div>

            {/* Active indicator */}
            {isActive && (
                <Check className="w-4 h-4 text-primary flex-shrink-0" />
            )}
        </button>
    );
}

export default ProfileSwitcher;
